import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CinemaRechargeModal from './components/RechargeModal';
import ShareRewardFloat from './components/ShareRewardFloat';
import { createMemberPaymentTicket, createMemberRechargeOrder, fetchAdvertisements, fetchHomeDisplayConfig, fetchMemberPaymentOptions, queryMemberRechargeOrder, type PublicAdvertisement } from '@/api/member';
import {
  buildSessionFromLogin,
  clearReferralCode,
  consumeMemberLoginReturn,
  ensureMemberAccessToken,
  readMemberSession,
  refreshCurrentMember,
  rememberMemberLoginReturn,
  saveMemberSession,
  type MemberSession,
} from '@/utils/memberSession';
import { shouldUseMobileExperience } from '@/utils/device';
import { consumeTelegramOIDCCallback, hasTelegramOIDCCallback } from '@/utils/telegramAuth';
import { normalizeInterfaceLanguage, supportedLanguages, type InterfaceLanguage } from '@/i18n/languages';

const bottomNavItems = [
  { id: 'home', labelKey: 'cinema_navHome', icon: 'ri-home-5-line', activeIcon: 'ri-home-5-fill', path: '/' },
  { id: 'ranking', labelKey: 'cinema_navCategories', icon: 'ri-trophy-line', activeIcon: 'ri-trophy-fill', path: '/ranking' },
  { id: 'mylist', labelKey: 'cinema_navMyList', icon: 'ri-fire-line', activeIcon: 'ri-fire-fill', path: '/mylist' },
  { id: 'ads', labelKey: 'cinema_navAds', icon: 'ri-gift-line', activeIcon: 'ri-gift-fill', path: '/ads' },
  { id: 'profile', labelKey: 'cinema_navProfile', icon: 'ri-user-line', activeIcon: 'ri-user-fill', path: '/profile' },
];

export default function CinemaLayout() {
  const { t, i18n } = useTranslation('cinema');
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [member, setMember] = useState<MemberSession>(() => readMemberSession());
  const [navHidden, setNavHidden] = useState(false);
  const [mobileExperience, setMobileExperience] = useState(shouldUseMobileExperience);
  const [homeNavAdvertisements, setHomeNavAdvertisements] = useState<PublicAdvertisement[]>([]);
  const [creatorCenterEnabled, setCreatorCenterEnabled] = useState(false);
  const [siteLanguages, setSiteLanguages] = useState<InterfaceLanguage[]>(() => [...supportedLanguages]);
  const navHideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const syncDevice = () => setMobileExperience(shouldUseMobileExperience());
    syncDevice();
    window.addEventListener('resize', syncDevice, { passive: true });
    window.addEventListener('orientationchange', syncDevice, { passive: true });
    return () => {
      window.removeEventListener('resize', syncDevice);
      window.removeEventListener('orientationchange', syncDevice);
    };
  }, []);

  useEffect(() => {
    void fetchHomeDisplayConfig()
      .then((config) => {
        setCreatorCenterEnabled(config.creatorCenterEnabled !== false);
        const configured = (config.availableLanguages || [])
          .map<InterfaceLanguage | null>((item) => {
            const supported = supportedLanguages.find((language) => language.code === item.code);
            return supported ? { ...supported, label: item.label || supported.label, shortLabel: item.shortLabel || supported.shortLabel } : null;
          })
          .filter((item): item is InterfaceLanguage => item !== null);
        const enabled = configured.length > 0 ? configured : [...supportedLanguages];
        setSiteLanguages(enabled);

        const current = normalizeInterfaceLanguage(i18n.resolvedLanguage || i18n.language);
        if (!enabled.some((item) => item.code === current)) {
          const configuredDefault = normalizeInterfaceLanguage(config.defaultLanguage);
          const nextLanguage = enabled.some((item) => item.code === configuredDefault)
            ? configuredDefault
            : enabled[0].code;
          void i18n.changeLanguage(nextLanguage);
        }
      })
      .catch(() => setCreatorCenterEnabled(false));
  }, [i18n]);

  useEffect(() => {
    let cancelled = false;
    void fetchAdvertisements('home_nav')
      .then((items) => {
        if (!cancelled) setHomeNavAdvertisements(items);
      })
      .catch(() => {
        if (!cancelled) setHomeNavAdvertisements([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const openRecharge = () => {
      const current = readMemberSession();
      if (!current.isLoggedIn) {
        rememberMemberLoginReturn(location.pathname + location.search);
        navigate('/login');
        return;
      }
      setRechargeOpen(true);
    };
    window.addEventListener('cinema-open-recharge', openRecharge);
    return () => window.removeEventListener('cinema-open-recharge', openRecharge);
  }, [location.pathname, location.search, navigate]);

  useLayoutEffect(() => {
    if (!location.pathname.startsWith('/watch/')) return;

    const resetScroll = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    resetScroll();
    const frame = window.requestAnimationFrame(resetScroll);
    const timer = window.setTimeout(resetScroll, 80);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const clearNavTimer = () => {
      if (navHideTimerRef.current != null) {
        window.clearTimeout(navHideTimerRef.current);
        navHideTimerRef.current = null;
      }
    };

    const scheduleHide = () => {
      clearNavTimer();
      navHideTimerRef.current = window.setTimeout(() => {
        setNavHidden(true);
      }, 2000);
    };

    const isCinemaMainVideo = (target: EventTarget | null) => {
      return target instanceof HTMLVideoElement && target.dataset.cinemaMainVideo === 'true';
    };

    const handleVideoPlay = (event: Event) => {
      if (!isCinemaMainVideo(event.target)) return;
      setNavHidden(false);
      scheduleHide();
    };

    const handleVideoPause = (event: Event) => {
      if (!isCinemaMainVideo(event.target)) return;
      clearNavTimer();
      setNavHidden(false);
    };

    const revealNav = () => {
      setNavHidden(false);
      const playingMainVideo = document.querySelector<HTMLVideoElement>('video[data-cinema-main-video="true"]');
      if (playingMainVideo && !playingMainVideo.paused && !playingMainVideo.ended) {
        scheduleHide();
      }
    };

    document.addEventListener('play', handleVideoPlay, true);
    document.addEventListener('pause', handleVideoPause, true);
    document.addEventListener('ended', handleVideoPause, true);
    window.addEventListener('mousemove', revealNav, { passive: true });
    window.addEventListener('touchstart', revealNav, { passive: true });

    return () => {
      clearNavTimer();
      document.removeEventListener('play', handleVideoPlay, true);
      document.removeEventListener('pause', handleVideoPause, true);
      document.removeEventListener('ended', handleVideoPause, true);
      window.removeEventListener('mousemove', revealNav);
      window.removeEventListener('touchstart', revealNav);
    };
  }, []);

  useEffect(() => {
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname !== '/login') {
      rememberMemberLoginReturn(`${location.pathname}${location.search}${location.hash}`);
    }
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const sync = () => setMember(readMemberSession());
    window.addEventListener('member-session-change', sync);
    void refreshCurrentMember().then(setMember).catch(sync);
    return () => window.removeEventListener('member-session-change', sync);
  }, []);

  useEffect(() => {
    if (!hasTelegramOIDCCallback()) return;
    let cancelled = false;
    void consumeTelegramOIDCCallback()
      .then(async (result) => {
        if (!result || cancelled) return;
        saveMemberSession(buildSessionFromLogin(result));
        if (result.isNewUser) clearReferralCode();
        const current = await refreshCurrentMember().catch(() => readMemberSession());
        if (cancelled) return;
        setMember(current);
        navigate(consumeMemberLoginReturn(), { replace: true });
      })
      .catch((error) => {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : 'Telegram 登录失败';
        navigate(`/login?auth_error=${encodeURIComponent(message)}`, { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' && !location.search;
    }
    if (path === '/ranking') {
      return location.pathname === '/ranking' || location.pathname === '/categories';
    }
    return location.pathname === path;
  };

  const activeBottomNav = bottomNavItems.find((item) => {
    if (item.id === 'home') return location.pathname === '/' && !location.search;
    if (item.id === 'ranking') return location.pathname === '/ranking' || location.pathname === '/categories';
    return location.pathname === item.path;
  })?.id ?? 'home';
  const isHotPage = location.pathname === '/mylist';
  const isWatchPage = location.pathname.startsWith('/watch/');
  const isEarnPage = location.pathname === '/ads';
  const isLoginPage = location.pathname === '/login';

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div className={`min-h-screen bg-background-950 text-foreground-50 ${mobileExperience ? 'pb-16' : 'pb-0'}`}>
      {/* Navbar */}
      <nav
        className={`${isHotPage && mobileExperience ? 'hidden' : 'block'} ${mobileExperience ? 'absolute' : 'fixed'} top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || searchOpen
            ? 'bg-background-950/95 backdrop-blur-xl border-b border-background-900/80'
            : 'bg-gradient-to-b from-background-950/90 via-background-950/40 to-transparent'
        } ${navHidden && !searchOpen ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'}`}
      >
        <div className="flex items-center justify-between h-14 md:h-16 px-4 md:px-10 lg:px-14">
          {/* Left: Logo + Desktop Nav */}
          <div className="flex items-center gap-6 md:gap-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 cursor-pointer group"
            >
              <img
                src="/images/logo-brand.webp"
                alt={t('cinema_brand')}
                className="h-10 md:h-12 w-auto max-w-[124px] md:max-w-[150px] object-contain flex-shrink-0"
              />
            </button>

            <div className={`${mobileExperience ? 'hidden' : 'flex'} items-center gap-1`}>
              <button
                onClick={() => navigate('/')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  isActive('/')
                    ? 'text-foreground-50 bg-foreground-50/10'
                    : 'text-foreground-400 hover:text-foreground-200'
                }`}
              >
                {t('cinema_navHome')}
              </button>
              <button
                onClick={() => navigate('/ranking')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  isActive('/ranking')
                    ? 'text-foreground-50 bg-foreground-50/10'
                    : 'text-foreground-400 hover:text-foreground-200'
                }`}
              >
                {t('cinema_navCategories')}
              </button>
              <button
                onClick={() => navigate('/mylist')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  isActive('/mylist')
                    ? 'text-foreground-50 bg-foreground-50/10'
                    : 'text-foreground-400 hover:text-foreground-200'
                }`}
              >
                {t('cinema_navMyList')}
              </button>
              <button
                onClick={() => navigate('/ads')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  isActive('/ads')
                    ? 'text-foreground-50 bg-foreground-50/10'
                    : 'text-foreground-400 hover:text-foreground-200'
                }`}
              >
                {t('cinema_navAds')}
              </button>
              {creatorCenterEnabled && (
                <button
                  onClick={() => navigate('/creator-center')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    isActive('/creator-center')
                      ? 'text-foreground-50 bg-foreground-50/10'
                      : 'text-foreground-400 hover:text-foreground-200'
                  }`}
                >
                  {t('cinema_creatorCenter')}
                </button>
              )}
              {homeNavAdvertisements.map((advertisement) => (
                <a
                  key={advertisement.id}
                  href={advertisement.targetUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={advertisement.description || advertisement.title}
                  className="flex h-8 max-w-32 shrink-0 items-center justify-center px-3 text-sm font-medium text-accent-400 transition-colors hover:text-accent-300"
                >
                  {advertisement.displayType === 'image' && advertisement.imageUrl ? (
                    <img
                      src={advertisement.imageUrl}
                      alt={advertisement.title}
                      className="max-h-7 max-w-28 object-contain"
                    />
                  ) : (
                    <span className="truncate">{advertisement.title}</span>
                  )}
                </a>
              ))}
            </div>
          </div>

          {/* Right: Search + User */}
          <div className="flex items-center gap-2 md:gap-3">
            <label className="relative flex h-9 items-center rounded-full text-foreground-400 hover:bg-foreground-50/5 hover:text-foreground-200" title={t('cinema_language')}>
              <i className="ri-global-line pointer-events-none absolute left-2.5 text-base" />
              <select
                value={normalizeInterfaceLanguage(i18n.resolvedLanguage || i18n.language)}
                onChange={(event) => void i18n.changeLanguage(event.target.value)}
                aria-label={t('cinema_language')}
                className="h-9 max-w-[5.25rem] cursor-pointer appearance-none bg-transparent pl-8 pr-2 text-xs outline-none md:max-w-[8.5rem]"
              >
                {siteLanguages.map((language) => (
                  <option key={language.code} value={language.code} className="bg-background-900 text-foreground-100">
                    {mobileExperience ? language.shortLabel : language.label}
                  </option>
                ))}
              </select>
            </label>
            {/* Search */}
            {searchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center gap-1 animate-ao-fade-in-up">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('cinema_searchPlaceholder')}
                    className="w-44 md:w-64 h-9 pl-9 pr-3 rounded-full bg-background-900/80 border border-background-800/80 text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/30 transition-all"
                    autoFocus
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
                    <i className="ri-search-line text-sm"></i>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                  className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:text-foreground-300 hover:bg-background-900 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </form>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-200 hover:bg-foreground-50/5 transition-all cursor-pointer"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
            )}

            {/* Desktop Login / Profile */}
            {member.isLoggedIn ? (
              <button
                onClick={() => navigate('/profile')}
                className={`${mobileExperience ? 'hidden' : 'flex'} items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-foreground-200 hover:text-foreground-50 hover:bg-foreground-50/8 transition-all whitespace-nowrap cursor-pointer`}
              >
                {member.avatar ? (
                  <img src={member.avatar} alt={member.name} className="h-6 w-6 rounded-full object-cover" />
                ) : (
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-500/20 text-accent-400">
                    <i className="ri-user-line text-xs"></i>
                  </span>
                )}
                <span className="max-w-24 truncate">{member.name}</span>
                <span className="text-accent-400">{member.pointsBalance}</span>
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className={`${mobileExperience ? 'hidden' : 'flex'} items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium text-foreground-300 hover:text-foreground-50 hover:bg-foreground-50/8 transition-all whitespace-nowrap cursor-pointer`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                {t('cinema_login')}
              </button>
            )}

            {/* Desktop VIP */}
            <button
              onClick={() => {
                if (member.isDealer) {
                  navigate('/distributor/dashboard');
                  return;
                }
                if (!member.isLoggedIn) {
                  navigate('/login');
                  return;
                }
                setRechargeOpen(true);
              }}
              className={`${mobileExperience ? 'hidden' : 'flex'} items-center gap-1 px-4 py-1.5 rounded-full text-sm font-semibold text-background-950 bg-[#F19744] hover:bg-[#F19744]/90 transition-all whitespace-nowrap cursor-pointer`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-vip-crown-line"></i>
              </span>
              {member.isDealer ? '分销商后台' : t('cinema_vip')}
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className={isHotPage ? 'min-h-0' : 'min-h-screen'}>
        <Outlet />
      </main>

      {!isHotPage && !(mobileExperience && (isWatchPage || isEarnPage)) && <ShareRewardFloat />}

      {/* Footer */}
      <footer className={`${isHotPage || (isLoginPage && mobileExperience) ? 'hidden' : 'block'} border-t border-background-900/80 bg-background-950`}>
        <div className="px-4 md:px-10 lg:px-14 py-8 md:py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
            <div className="flex items-center gap-2">
              <img
                src="/images/logo-brand.webp"
                alt={t('cinema_brand')}
                className="h-10 w-auto max-w-[124px] object-contain flex-shrink-0"
              />
            </div>
            <p className="text-[10px] md:text-xs text-foreground-600 text-center leading-relaxed">
              {t('cinema_footerWarning')}
            </p>
          </div>
        </div>
      </footer>

      {/* Bottom Mobile Nav */}
      {mobileExperience && <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background-950/95 backdrop-blur-xl border-t border-background-900/80 safe-area-pb">
        <div className="flex items-center justify-around h-16">
          {bottomNavItems.map((item) => {
            const active = activeBottomNav === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                }}
                className="flex flex-col items-center justify-center gap-0.5 w-16 h-14 cursor-pointer"
              >
                <span className={`w-5 h-5 flex items-center justify-center text-lg transition-colors ${active ? 'text-[#F97316]' : 'text-foreground-500'}`}>
                  <i className={active ? item.activeIcon : item.icon}></i>
                </span>
                <span className={`text-[10px] transition-colors ${active ? 'text-[#F97316] font-medium' : 'text-foreground-500'}`}>
                  {t(item.labelKey)}
                </span>
              </button>
            );
          })}
        </div>
      </nav>}
      {/* Recharge Modal */}
      <CinemaRechargeModal
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        loadOptions={async () => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return fetchMemberPaymentOptions(token);
        }}
        createTicket={async (packageId, payMethod) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return createMemberPaymentTicket(packageId, payMethod, token);
        }}
        createOrder={async (packageId, payMethod) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return createMemberRechargeOrder(packageId, payMethod, token);
        }}
        queryOrder={async (outTradeNo) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return queryMemberRechargeOrder(outTradeNo, token);
        }}
        onRechargeSuccess={() => {
          void refreshCurrentMember().then(setMember);
        }}
      />
    </div>
  );
}
